/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Image } from '@fluentui/react/lib/Image';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { AudioPlayer } from 'src/core/components/AudioPlayer';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { getDuration, getSentiment } from 'src/languageStudio/appShell/prebuiltDemos/utils/callCenterUtils';
import { CallCenterDemoSample } from 'src/languageStudio/appShell/prebuiltDemos/utils/demoSamples';
import { AnalyzedSentence } from 'src/packages/analyzed-conversation-viewer/lib/esm';

const messages = defineMessages({
    callTranscriptSectionTitle: {
        id: 'languageStudio.callCenterResults.callTranscriptSectionTitle',
        defaultMessage: 'Transcript with speaker separation'
    },
    callTranscriptionSectionTextDesc: {
        id: 'languageStudio.callCenterResults.callTranscriptionSectionTextDesc',
        defaultMessage: 'Showing original uploaded text file (.txt).'
    },
    callTranscriptionAudioLearnMore: {
        id: 'languageStudio.callCenterResults.callTranscriptionAudioLearnMore',
        defaultMessage: 'Learn more about conversation transcription'
    },
    callTranscriptionAudioDesc: {
        id: 'languageStudio.callCenterResults.callTranscriptionAudioDesc',
        defaultMessage: `Conversation transcription is a speech-to-text solution that provides real-time or asynchronous
        transcription of any conversation which was used as input to text analytics API in this sample. Channel
        diarization is used for speaker separation for stereo audio and diarization analysis is used for mono
        channel audio.`
    }
});

const theme = getScopedTheme('CallCenterDemoPage');
const containerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { border: `1px solid ${theme.borderColor}`, height: '450px' }
});
const headerContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { padding: '10px 12px', borderBottom: `1px solid ${theme.borderColor}` }
});

export const CallTranscriptionContent = ({ selectedSample }: { selectedSample: CallCenterDemoSample }) => {
    const { localize } = useLocalization();

    const sentences =
        selectedSample?.output.transcription.recognizedPhrases.map((phrase, phraseIndex) => ({
            text: phrase.nBest[0].display,
            duration: getDuration(phrase.duration),
            sentiment: getSentiment(phrase.nBest[0].sentiment),
            speakerTitle: `Speaker ${phrase.speaker ?? phrase.channel}`,
            entities: selectedSample?.output.conversationAnalyticsResults.conversationPiiResults.conversations[0].conversationItems[
                phraseIndex
            ].entities.map(e => ({ ...e, offset: e.offset + 1 })),
            redactedText:
                selectedSample?.output.conversationAnalyticsResults.conversationPiiResults.conversations[0].conversationItems[phraseIndex]
                    .redactedContent.text
        })) ?? [];

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            <LuisText>
                {selectedSample?.sampleType === 'audio'
                    ? localize(messages.callTranscriptionAudioDesc)
                    : localize(messages.callTranscriptionSectionTextDesc)}
            </LuisText>
            {selectedSample?.sampleType === 'audio' && <AudioPlayer audioSrc={selectedSample.audioLink} />}
            <Stack styles={containerStyles} tokens={{ childrenGap: 20 }}>
                {!selectedSample ? (
                    <Stack grow horizontalAlign="center" verticalAlign="center">
                        <Image src={getAssetsUrl(`assets/images/customText/newEmptyState.svg`)} />
                    </Stack>
                ) : selectedSample?.sampleType === 'audio' ? (
                    <Stack styles={{ root: { overflow: 'hidden' } }}>
                        <Stack verticalAlign="center" styles={headerContainerStyles}>
                            <LuisText>{localize(messages.callTranscriptSectionTitle)}</LuisText>
                        </Stack>
                        <Stack grow styles={{ root: { overflowY: 'auto' } }}>
                            {sentences.map(sentence => (
                                <AnalyzedSentence hidePii={false} showSentiment={false} sentence={{ ...sentence, entities: [] }} />
                            ))}
                        </Stack>
                    </Stack>
                ) : (
                    [
                        <Stack verticalAlign="center" styles={headerContainerStyles}>
                            <LuisText>.txt</LuisText>
                        </Stack>,
                        <Stack grow styles={{ root: { overflowY: 'auto', padding: '0px 20px 30px 20px' } }}>
                            <LuisText styles={{ root: { whiteSpace: 'pre-wrap' } }}>{selectedSample?.textInput}</LuisText>
                        </Stack>
                    ]
                )}
            </Stack>
        </Stack>
    );
};
